import { Metamodel } from '../base/model';
import { Activity } from './activity';

import { format } from 'app/shared/utils/string.util';
import {
    AzureBlobStorageLinkedService,
    SnowflakeLinkedService,
    IAttribute,
    IModel,
    IViolation,
    LinkedService,
    Owned,
    ParameterValue,
    stringIsNullOrEmpty,
    snowflakeAuthTypes,
    azureStorageAuthTypes,
    validateLinkedServicePermission,
    IValidationContext,
    ModelConstants,
    arrayIsNullOrEmpty,
    SnowflakeV2LinkedService,
    IVersioningActivity,
    Collection,
    ParameterConfig
} from 'shell-models-lib';
import { StagingSettings } from './staging-settings';
import { validatePathInStagingAndLoggingSettings } from 'app/shared/utils/activity-validation.util';
import { PlainScript } from './plain-script';
import { OracleLinkedService } from '../linkedservice/oracle.linkedservice';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { AzurePostgreSqlLinkedService } from '../linkedservice/azure-db-postgre.linkedservice';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { hasSnowV2ScriptParam } from 'app/shared/utils/activity-settings.util';
import { Direction } from 'app/shared/components/parameter-configure/parameter-configure.const';
import { azurePostgreSQLV2ScriptandUpsertVersion, validateIRVersion } from 'app/shared/utils/version.check.util';
import { Copier } from 'app/model/base/copier';

export type LoggingMode = 'ActivityOutput' | 'ExternalStore';

@Metamodel.model({
    name: ModelConstants.scriptActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.ScriptLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.ScriptLabel)
    ],
    referable: true
})
export class ScriptActivity extends Activity implements IVersioningActivity {
    public static readonly MODEL: IModel;
    public static readonly PLAIN_SCRIPTS: IAttribute;
    public static readonly LOG_DESTINATION: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly SCRIPT_BLOCK_EXECUTION_TIMEOUT: IAttribute;
    public static readonly DATABASE: IAttribute;
    public static readonly LINKED_SERVICE_PROPERTIES: IAttribute;
    public static readonly TREAT_DECIMAL_AS_STRING: IAttribute;

    @Metamodel.attribute({
        display: !__IS_FABRIC_DPE__ ? stringResources.LinkedService : stringResources.ConnectionLabel,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            required: true,
            validator: ScriptActivity._validateScriptLinkedService
        }
    })
    public linkedService: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public dmtsCredentialForArtifact?: LinkedService;

    public linkedServiceType?: string;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: stringResources.ScriptLabel,
        accepts: ['Object', 'Array'],
        association: 'PLAIN_SCRIPTS',
        constraint: {
            validator: ScriptActivity._validatePlainScript
        }
    })
    public plainScripts?: PlainScript[];

    @Metamodel.attribute({
        display: stringResources.ScriptActivity_BlockExecutionTimeout_Label,
        association: 'SCRIPT_BLOCK_EXECUTION_TIMEOUT',
        templatable: true,
        accepts: ['String'],
        constraint: {
            minimum: 1,
            maximum: 1440
        }
    })
    public scriptBlockExecutionTimeout?: number = 120;

    public enableLog: boolean = false;

    @Metamodel.attribute({
        display: stringResources.ScriptActivity_LogOutput_Title,
        type: StagingSettings.MODEL,
        association: 'LOG_DESTINATION',
        accepts: ['String'],
        templatable: true
    })
    public logDestination: LoggingMode = 'ExternalStore';

    @Metamodel.attribute({
        display: stringResources.LoggingSettingsLabel,
        type: StagingSettings.MODEL,
        constraint: {
            validator: ScriptActivity._validateStagingSetting
        }
    })
    public logStorageSettings?: StagingSettings = Owned.create(StagingSettings.MODEL, this);

    @Metamodel.attribute({
        display: stringResources.Database,
        templatable: true,
        association: 'DATABASE'
    })
    public database?: string;

    @Metamodel.attribute({
        display: stringResources.TreatDecimalAsString,
        association: 'TREAT_DECIMAL_AS_STRING',
        templatable: true
    })
    public treatDecimalAsString?: boolean = false;

    @Metamodel.attribute({
        display: stringResources.VersionLabel,
    })
    public connectionVersion?: string;

    /**
     * Addition properties for referenced linked service in fabric.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PROPERTIES'
    })
    public linkedServiceProperties: ParameterConfig[] = [];

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static async _validateScriptLinkedService(
        activity: ScriptActivity,
        ls: LinkedService,
        violation?: IViolation,
        context?: IValidationContext
    ): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];
        if (ls) {
            if (__IS_FABRIC_DPE__) {
                const lsModel = Metamodel.modelOf(ls);
                const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                    context.contextData,
                    'LinkedServiceRegistryService'
                );
                const metadata = linkedServiceRegistry.getLinkedServiceConfiguration(lsModel.name);
                if (!metadata.supportScriptActivity) {
                    violations.push({
                        message: () =>
                            format(
                                stringResources.ActivityNotSupportLinkedServiceError,
                                ScriptActivity.MODEL.display[0],
                                metadata.displayName
                            ),
                        messageType: 'unsupported'
                    }
                    );
                }
            }

            if (ls instanceof SnowflakeLinkedService && ls.authenticationType === snowflakeAuthTypes.OAuth) {
                violations.push({
                    context: activity,
                    message: () => stringResources.ScriptActivity_SnowflakeLS_OauthAuth_ErrorMessage
                });
            }
            if (!__IS_FABRIC_DPE__ && ls instanceof AzurePostgreSqlLinkedService) {
                if (ls.version !== driverVersions.v2) {
                    violations.push({
                        context: activity,
                        message: () =>
                            format(
                                stringResources.ScriptActivity_Connector_Legacy_ErrorMessage,
                                '1.0',
                                stringResources.VersionLabel.toLocaleLowerCase(),
                                '2.0'
                            )
                    });
                }
                const integrationRuntime = ls.integrationRuntime;
                const shirMeetRequirements = await validateIRVersion(integrationRuntime, azurePostgreSQLV2ScriptandUpsertVersion, 5, context);
                if (!shirMeetRequirements) {
                    violations.push({
                        context: activity,
                        message: () => format(
                            stringResources.New_Connector_Required_IR,
                            stringResources.ScriptLabel.toLocaleLowerCase(),
                            azurePostgreSQLV2ScriptandUpsertVersion
                        )
                    });
                }

            }
            violations.push(...validateLinkedServicePermission(ls, context));
        }

        return violations;
    }

    /**
     * _validatePlainScript
     * @param activity: ScriptActivity
     * @returns IViolation[]
     * validates Script section of Settings tab, specifically:
     *   - must have at least 1 script
     *   - must specifies Query
     *   - optional parameters, though if defined, Name & Type cannot be empty
     *
     * index is required in context, which locates the violation.
     * for parameters, field & parentIdx are also required to properly locate the violation.
     */
    private static _validatePlainScript(
        activity: ScriptActivity,
        ls: LinkedService,
        violation?: IViolation,
        context?: IValidationContext
    ): IViolation[] {
        const violations: IViolation[] = [];
        let hasScriptParams = true;
        if (activity.linkedService instanceof SnowflakeV2LinkedService) {
            hasScriptParams = hasSnowV2ScriptParam(__IS_FABRIC_DPE__ ? activity.connectionVersion : activity.linkedService.version);
        }
        if (activity && activity.plainScripts) {
            if (activity.plainScripts.length === 0) {
                violations.push({
                    context: activity,
                    message: () => format(stringResources.RequiredTemplate, stringResources.ScriptLabel)
                });
            }
            let hasInvalidText = false;
            activity.plainScripts.forEach((script, index) => {
                if (
                    !hasInvalidText &&
                    stringIsNullOrEmpty(script.text) &&
                    !PlainScript.SCRIPT_TEXT.templateValue(script)
                ) {
                    violations.push({
                        context: {
                            ...script,
                            index
                        },
                        message: () => format(stringResources.RequiredTemplate, stringResources.QueryLabel)
                    });
                    hasInvalidText = true;
                }
                if (hasScriptParams && script.scriptParams && script.scriptParams.length > 0) {
                    const nameSet = new Set();
                    let sizeIdx = 0; // sizeIdx may be different from paramIdx, as size input isn't always visible
                    script.scriptParams.forEach(async (param, paramIdx) => {
                        // validate name - name can be empty as backend will backfill, though it cannot be duplicated.
                        if (nameSet.has(param.name)) {
                            violations.push({
                                message: () => stringResources.ParameterConfiguration_Error_DuplicateName,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'name'
                                }
                            });
                        } else if (!stringIsNullOrEmpty(param.name)) {
                            nameSet.add(param.name);
                        }

                        // validate type
                        if (stringIsNullOrEmpty(param.type)) {
                            violations.push({
                                message: () => stringResources.Activity_Error_Configuration_InvalidType,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'type'
                                }
                            });
                        }

                        // validate direction
                        if (stringIsNullOrEmpty(param.direction)) {
                            violations.push({
                                message: () => stringResources.Activity_Error_Configuration_InvalidDirection,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'direction'
                                }
                            });
                        }

                        // Datetimeoffset does not support Oracle/Snowflake
                        if (
                            param.type === 'Datetimeoffset' &&
                            (activity.linkedService instanceof OracleLinkedService ||
                                activity.linkedService instanceof SnowflakeLinkedService)
                        ) {
                            violations.push({
                                message: () => stringResources.ScriptActivity_DateOffsetDirection_Invalid,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'type'
                                }
                            });
                        }

                        // Int64  does not support Oracle
                        if (param.type === 'Int64' && activity.linkedService instanceof OracleLinkedService) {
                            violations.push({
                                message: () => stringResources.ScriptActivity_Int64Direction_Invalid,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'type'
                                }
                            });
                        }

                        // Byte[] only works with Output
                        if (param.type === 'Byte[]' && param.direction !== 'Output') {
                            violations.push({
                                message: () => stringResources.ScriptActivity_ByteDirection_Invalid,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'direction'
                                }
                            });
                        }

                        // Snowflake V2 only supports "Input" direction
                        if (param.direction !== Direction.Input && activity.linkedService instanceof SnowflakeV2LinkedService) {
                            const lsModel = Metamodel.modelOf(activity.linkedService);
                            const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                                context.contextData,
                                'LinkedServiceRegistryService'
                            );
                            const metadata = linkedServiceRegistry.getLinkedServiceConfiguration(lsModel.name);
                            violations.push({
                                message: () => format(stringResources.ScriptActivity_LsDirection_Invalid, metadata.displayName),
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: paramIdx,
                                    field: 'direction'
                                }
                            });
                        }

                        // validate size: required if direction is output or inputouput and type is string or byte[]
                        if (
                            param.type === 'String' &&
                            (param.direction === Direction.Output || param.direction === Direction.InputOutput) &&
                            param.size == null
                        ) {
                            violations.push({
                                message: () => stringResources.ScriptActivity_Size_Header_Description,
                                context: {
                                    ...param,
                                    parentIdx: index,
                                    index: sizeIdx,
                                    field: 'size'
                                }
                            });
                        } else if (param.size != null) {
                            sizeIdx += 1; // increment sizeIdx as there is a valid size input
                        }
                    });
                }
            });
        }
        return violations;
    }

    private static _validateStagingSetting(
        activity: ScriptActivity,
        value?: StagingSettings,
        violation?: IViolation,
        context?: IValidationContext
    ): IViolation[] | undefined {
        const result: IViolation[] = [];
        if (activity.enableLog && activity.logDestination && activity.logDestination === 'ExternalStore') {
            if (!activity.logStorageSettings || activity.logStorageSettings.storageLinkedService == null) {
                return [
                    {
                        context: activity,
                        message: () =>
                            format(
                                stringResources.DeleteLoggingSetting_ErrorMessage_MissingLinkedService,
                                __IS_FABRIC_DPE__
                                    ? stringResources.ConnectionLabel.toLocaleLowerCase()
                                    : stringResources.LinkedServiceLowerCase
                            )
                    }
                ];
            } else {
                if (
                    activity.logStorageSettings.storageLinkedService instanceof AzureBlobStorageLinkedService &&
                    activity.logStorageSettings.storageLinkedService.authenticationMethod ===
                    azureStorageAuthTypes.Anonymous
                ) {
                    result.push({
                        context: activity,
                        message: () =>
                            format(
                                stringResources.Anonymous_BlobStorage_ErrorTemplate,
                                stringResources.LoggingSettingsLabel.toLocaleLowerCase()
                            )
                    });
                } else if (activity.logStorageSettings.storageLinkedService) {
                    result.push(
                        ...validateLinkedServicePermission(activity.logStorageSettings.storageLinkedService, context)
                    );
                }

                result.push(...validatePathInStagingAndLoggingSettings(activity.logStorageSettings));
            }
        }
        return result;
    }

    public updateLinkedService(value: {
        linkedService: LinkedService;
        linkedServiceType?: string;
        dmtsCredentialLinkedService?: LinkedService;
    }): void {
        this.linkedService = value?.linkedService;
        this.dmtsCredentialForArtifact = value?.dmtsCredentialLinkedService;
        this.linkedServiceType = value?.linkedServiceType;
        if (arrayIsNullOrEmpty(this.plainScripts)) {
            this.plainScripts = [new PlainScript()];
        }
    }

    public shouldHonorConnectionVersion(): boolean {
        if (!__IS_FABRIC_DPE__) {
            return false;
        }
        if (this.linkedService instanceof AzurePostgreSqlLinkedService) {
            return this.connectionVersion === '2.0';
        }
        return false;
    }

    public onActivityCloned(clonedActivity: ScriptActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.dmtsCredentialForArtifact?.parameterized || this.linkedService?.parameterized) {
                if (this.dmtsCredentialForArtifact != null) {
                    clonedActivity.dmtsCredentialForArtifact = Copier.clone(
                        context,
                        [this.dmtsCredentialForArtifact],
                        undefined,
                        true
                    )[0] as LinkedService;
                }
                if (this.linkedService != null) {
                    clonedActivity.linkedService = Copier.clone(
                        context,
                        [this.linkedService],
                        undefined,
                        true
                    )[0] as LinkedService;
                }
            }
        }
    }
}
